import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from '../../assets/img/roboman-logo.png';

/**
 * PRO Header — inspired by premium SaaS (instantly / prosp vibes)
 * - Same functionality (anchors + routes) — no deps added
 * - Polished glass, gradient rails, pill nav, mobile sheet
 * - Accepts optional `isScrolled` to tighten on scroll
 */
const Header = ({ isScrolled = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [atTop, setAtTop] = useState(true);
  const [active, setActive] = useState('');

  useEffect(() => {
    const onScroll = () => setAtTop(window.scrollY < 8);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNavToSection = (sectionId) => {
    setActive(sectionId);
    if (location.pathname !== '/') navigate('/');
    setTimeout(() => {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 100);
    setOpen(false);
  };

  const links = [
    { id: 'features', label: 'Features' },
    { id: 'pricing', label: 'Pricing' },
    { id: 'testimonials', label: 'Testimonials' },
  ];

  return (
    <header role="banner" className="sticky top-0 z-50">
      {/* announcement rail */}
      <div className="hidden sm:block bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 text-white/95 text-[11px] tracking-wide">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-8 flex items-center justify-between">
          <p className="font-semibold">New: Multichannel Autopilot — email · LinkedIn · voice</p>
          <button onClick={() => handleNavToSection('features')} className="underline/30 hover:underline font-semibold">See what’s new →</button>
        </div>
      </div>

      {/* main bar */}
      <div
        className={[
          'backdrop-blur-xl transition-all duration-300 border-b',
          (isScrolled || !atTop)
            ? 'bg-white/85 border-slate-200 shadow-[0_8px_30px_-12px_rgba(2,6,23,0.25)]'
            : 'bg-white/55 border-transparent',
        ].join(' ')}
      >
        {/* thin gradient line */}
        <div className="h-[2px] w-full bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500 opacity-60" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded-md"
              aria-label="Go home"
            >
              <img src={Logo} alt="Roboman" className="h-6 w-auto" />
              <span className="sr-only">Roboman</span>
            </button>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-2" aria-label="Primary">
              {links.map((l) => (
                <button
                  key={l.id}
                  onClick={() => handleNavToSection(l.id)}
                  data-active={active === l.id}
                  className="relative inline-flex items-center justify-center px-3 py-2 rounded-full text-sm font-medium text-slate-700 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 group"
                >
                  {/* pill bg on hover/active */}
                  <span className="absolute inset-0 rounded-full bg-slate-900/0 group-hover:bg-slate-900/[.04] transition" />
                  <span className="relative z-10">{l.label}</span>
                  <span className="absolute left-3 right-3 -bottom-[2px] h-[2px] origin-left scale-x-0 group-hover:scale-x-100 transition-transform bg-gradient-to-r from-indigo-500 to-fuchsia-500" />
                </button>
              ))}
            </nav>

            {/* Actions */}
            <div className="hidden md:flex items-center gap-3">
              <button
                onClick={() => navigate('/login')}
                className="text-sm font-semibold text-slate-700 hover:text-slate-900 px-4 py-2 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
              >
                Login
              </button>
              <button
                onClick={() => navigate('/signup')}
                className="relative inline-flex items-center justify-center overflow-hidden rounded-full px-5 py-2 text-sm font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600" />
                <span className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity bg-[radial-gradient(100%_100%_at_50%_0%,rgba(255,255,255,.35)_0,rgba(255,255,255,0)_60%)]" />
                <span className="relative">Sign Up Free</span>
              </button>
            </div>

            {/* Mobile toggle */}
            <button
              onClick={() => setOpen((v) => !v)}
              className="md:hidden inline-flex items-center justify-center h-9 w-9 rounded-lg ring-1 ring-slate-300 text-slate-700 hover:bg-white/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
              aria-label="Toggle menu"
              aria-expanded={open}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {open ? (
                  <path d="M18 6 6 18M6 6l12 12" />
                ) : (
                  <>
                    <line x1="3" y1="6" x2="21" y2="6" />
                    <line x1="3" y1="12" x2="21" y2="12" />
                    <line x1="3" y1="18" x2="21" y2="18" />
                  </>
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile sheet */}

        {/* Mobile sheet */}
{open && (
  <div className="md:hidden transition-all duration-300 opacity-100" aria-hidden={false}>
    <div className="px-4 pb-4">
            <div className="mt-2 rounded-2xl bg-white/90 backdrop-blur-xl ring-1 ring-slate-200 shadow-xl p-4">
              <nav className="grid gap-2" aria-label="Mobile">
                {links.map((l) => (
                  <button
                    key={l.id}
                    onClick={() => handleNavToSection(l.id)}
                    className="text-base text-slate-800 bg-white/80 ring-1 ring-slate-200 rounded-xl px-4 py-3 text-left hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                  >
                    {l.label}
                  </button>
                ))}
              </nav>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <button
                  onClick={() => { setOpen(false); navigate('/login'); }}
                  className="w-full rounded-xl ring-1 ring-slate-200 px-4 py-3 text-sm font-semibold text-slate-800 bg-white hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                >
                  Login
                </button>
                <button
                  onClick={() => { setOpen(false); navigate('/signup'); }}
                  className="w-full rounded-xl px-4 py-3 text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                >
                  Sign Up Free
                </button>
              </div>
              
            </div>
          </div>
        
        
        
        </div>
 )} </div>
    </header>
  );
};

export default Header;
